import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, Menu, X, ChevronDown, ArrowLeft, Briefcase, User, Send } from 'lucide-react';
import './index.css';

const Portfolio = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [currentPage, setCurrentPage] = useState('main');
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const [formStatus, setFormStatus] = useState('idle');

  useEffect(() => {
    const handleScroll = () => {
      if (currentPage !== 'main') return;
      
      const sections = ['home', 'about', 'projects', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const navigateToPage = (page) => {
    setCurrentPage(page);
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');

    const form = e.target;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => {
          setIsContactFormOpen(false);
          setFormStatus('idle');
        }, 3000);
      } else {
        console.error('Form submission error:', data);
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Network error:', error);
      setFormStatus('error');
    }
  };

  const professionalProjects = [
    {
      title: "ISO27001 & ISO42001 Implementation",
      description: "Successfully maintained and developed joint ISO42001 certification for client and parent company, enabling significant new government contracts.",
      tech: ["ISO27001", "ISO42001", "Compliance", "Risk Management"],
      type: "certification",
      impact: "Enabled £2M+ in new government contracts"
    },
    {
      title: "IASME Certification Body Establishment",
      description: "Established IASME Certification Body at BN-IS, achieving internal certifications including Cyber Essentials Plus and Cyber Assurance Level Two.",
      tech: ["IASME", "Cyber Essentials", "Cyber Assurance", "Certification"],
      type: "internal",
      impact: "Created new revenue stream for certification services"
    },
    {
      title: "Enterprise Cloud Security Transformation",
      description: "Supporting global expansion and SME to Enterprise transformation through comprehensive cloud security improvements and team development.",
      tech: ["Microsoft Azure", "Modern Work", "Security", "Compliance"],
      type: "transformation",
      impact: "Supported 300% company growth over 18 months"
    },
    {
      title: "CIS Controls Implementation",
      description: "Improved cyber security posture through ongoing risk assessments, implementing mitigations and developing robust Business Continuity and Disaster Recovery Plans.",
      tech: ["CIS Controls", "Risk Assessment", "BCP", "Disaster Recovery"],
      type: "security",
      impact: "Reduced security incidents by 85%"
    },
    {
      title: "Multi-Tenant Azure Environment Design",
      description: "Architected and implemented secure multi-tenant Azure environments for client segregation and compliance requirements.",
      tech: ["Azure", "Multi-tenant", "Security", "Architecture"],
      type: "architecture",
      impact: "Enabled scalable client onboarding process"
    },
    {
      title: "Zero Trust Security Implementation",
      description: "Led implementation of Zero Trust security model across enterprise infrastructure, including conditional access and device compliance policies.",
      tech: ["Zero Trust", "Conditional Access", "Intune", "Azure AD"],
      type: "security",
      impact: "Improved security posture by 70% measurable metrics"
    }
  ];

  const personalProjects = [
    {
      title: "Home Lab Kubernetes Cluster",
      description: "Built and maintain a 6-node Kubernetes cluster using Raspberry Pi 4s for learning container orchestration and DevOps practices.",
      tech: ["Kubernetes", "Raspberry Pi", "Docker", "Helm", "Prometheus"],
      type: "infrastructure",
      github: "https://github.com/wallwright/homelab-k8s",
      status: "Active"
    },
    {
      title: "Personal Cloud Security Dashboard",
      description: "Developed a React-based dashboard to monitor personal cloud infrastructure security posture across multiple cloud providers.",
      tech: ["React", "Node.js", "Azure API", "AWS SDK", "Chart.js"],
      type: "development",
      github: "https://github.com/wallwright/security-dashboard",
      status: "In Progress"
    },
    {
      title: "Automated Backup Solution",
      description: "Created automated backup solution for personal data using Python, encrypting and storing across multiple cloud providers for redundancy.",
      tech: ["Python", "Encryption", "Azure Blob", "AWS S3", "Automation"],
      type: "automation",
      github: "https://github.com/wallwright/cloud-backup",
      status: "Complete"
    },
    {
      title: "IoT Security Scanner",
      description: "Built a network scanner to identify and assess IoT devices on home networks, providing security recommendations and vulnerability assessments.",
      tech: ["Python", "Nmap", "MQTT", "SQLite", "Flask"],
      type: "security",
      github: "https://github.com/wallwright/iot-scanner",
      status: "Active"
    },
    {
      title: "Learning Management System",
      description: "Developed a personal learning management system to track cybersecurity certifications, study progress, and practice lab environments.",
      tech: ["Vue.js", "Express.js", "MongoDB", "Docker"],
      type: "development",
      status: "Complete"
    },
    {
      title: "Compliance Automation Scripts",
      description: "Collection of PowerShell and Python scripts for automating common compliance checks and reporting for small businesses.",
      tech: ["PowerShell", "Python", "Microsoft Graph", "Reporting"],
      type: "automation",
      github: "https://github.com/wallwright/compliance-tools",
      status: "Active"
    }
  ];

  const GeometricAvatar = () => (
    <div className="relative w-40 h-40 mx-auto mb-8">
      <div className="absolute inset-0 transform rotate-0">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <polygon 
            points="50 1 95 25 95 75 50 99 5 75 5 25" 
            fill="none" 
            stroke="url(#gradient1)" 
            strokeWidth="2"
            className="animate-pulse"
          />
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0ea5e9" />
              <stop offset="100%" stopColor="#06b6d4" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      
      <div className="absolute inset-4 transform rotate-30">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <polygon 
            points="50 1 95 25 95 75 50 99 5 75 5 25" 
            fill="rgba(14, 165, 233, 0.1)" 
            stroke="#0ea5e9" 
            strokeWidth="1.5"
          />
        </svg>
      </div>
      
      <div className="absolute inset-8 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-cyan-500/50 flex items-center justify-center shadow-2xl">
        <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-cyan-400 to-blue-400">
          WA
        </span>
      </div>
      
      <div className="absolute top-0 right-0 w-3 h-3 bg-cyan-400 rounded-full animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-2 h-2 bg-blue-400 rounded-full animate-pulse delay-75"></div>
    </div>
  );

  const ContactFormModal = () => (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-slate-100">Get In Touch</h3>
          <button
            onClick={() => {
              setIsContactFormOpen(false);
              setFormStatus('idle');
            }}
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {formStatus === 'success' ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="text-emerald-400" size={32} />
            </div>
            <h4 className="text-xl font-semibold text-slate-100 mb-2">Message Sent!</h4>
            <p className="text-slate-300">Thank you for reaching out. I'll get back to you soon.</p>
          </div>
        ) : (
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <input type="hidden" name="access_key" value="336545e5-47b0-46b6-9646-cc0fdbdf36f6" />
            <input type="hidden" name="subject" value="New Contact Form Submission from Portfolio" />
            <input type="hidden" name="from_name" value="Portfolio Contact Form" />

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:border-cyan-500 transition-colors"
                placeholder="your.email@example.com"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows="4"
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                placeholder="Your message..."
              ></textarea>
            </div>

            {formStatus === 'error' && (
              <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-3">
                <p className="text-red-300 text-sm">Failed to send message. Please try again.</p>
              </div>
            )}

            <button
              type="submit"
              disabled={formStatus === 'sending'}
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-cyan-500 hover:to-blue-500 transition-all duration-300 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {formStatus === 'sending' ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Send Message
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );

  const ProjectCard = ({ project, isProfessional = false }) => (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-cyan-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/10">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xl font-bold text-slate-100">{project.title}</h3>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            project.type === 'certification' ? 'bg-emerald-900/40 text-emerald-300 border border-emerald-700/40' :
            project.type === 'security' ? 'bg-red-900/40 text-red-300 border border-red-700/40' :
            project.type === 'transformation' || project.type === 'development' ? 'bg-blue-900/40 text-blue-300 border border-blue-700/40' :
            project.type === 'architecture' || project.type === 'infrastructure' ? 'bg-purple-900/40 text-purple-300 border border-purple-700/40' :
            project.type === 'automation' ? 'bg-amber-900/40 text-amber-300 border border-amber-700/40' :
            'bg-cyan-900/40 text-cyan-300 border border-cyan-700/40'
          }`}>
            {project.type}
          </span>
          {!isProfessional && project.github && (
            <a href={project.github} className="text-slate-300 hover:text-cyan-400 transition-colors">
              <Github size={16} />
            </a>
          )}
        </div>
      </div>
      
      <p className="text-slate-300 mb-4 text-sm leading-relaxed">
        {project.description}
      </p>
      
      {isProfessional && project.impact && (
        <p className="text-cyan-400 mb-4 text-sm font-medium">
          🎯 {project.impact}
        </p>
      )}
      
      {!isProfessional && project.status && (
        <p className={`mb-4 text-sm font-medium ${
          project.status === 'Active' ? 'text-emerald-400' :
          project.status === 'Complete' ? 'text-blue-400' :
          'text-amber-400'
        }`}>
          Status: {project.status}
        </p>
      )}
      
      <div className="flex flex-wrap gap-2">
        {project.tech.map((tech) => (
          <span key={tech} className="bg-slate-700/50 text-cyan-300 px-2 py-1 rounded border border-slate-600/50 text-xs">
            {tech}
          </span>
        ))}
      </div>
    </div>
  );

  const ProjectsPage = ({ title, projects, isProfessional, icon: Icon }) => (
    <div className="min-h-screen py-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigateToPage('main')}
            className="text-slate-200 hover:text-cyan-400 transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Back to Main
          </button>
        </div>
        
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-6">
            <Icon className="w-12 h-12 text-cyan-400" />
            <h1 className="text-4xl md:text-5xl font-bold text-slate-100">{title}</h1>
          </div>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            {isProfessional 
              ? "Showcasing key professional achievements and business impact across cloud security and compliance projects."
              : "Personal projects and experiments in cybersecurity, cloud infrastructure, and software development."}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <ProjectCard key={index} project={project} isProfessional={isProfessional} />
          ))}
        </div>
      </div>
    </div>
  );

  const mainProjects = [
    professionalProjects[0],
    professionalProjects[1],
    personalProjects[0],
    personalProjects[1]
  ];

  if (currentPage === 'professional') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <ProjectsPage 
          title="Professional Projects" 
          projects={professionalProjects} 
          isProfessional={true}
          icon={Briefcase}
        />
      </div>
    );
  }

  if (currentPage === 'personal') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <ProjectsPage 
          title="Personal Projects" 
          projects={personalProjects} 
          isProfessional={false}
          icon={User}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {isContactFormOpen && <ContactFormModal />}

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-slate-950/80 backdrop-blur-lg border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="text-2xl font-bold text-slate-100">
              WA
            </div>
            
            <div className="hidden md:flex space-x-8">
              {['home', 'about', 'projects', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`capitalize transition-colors duration-300 ${
                    activeSection === section 
                      ? 'text-cyan-400' 
                      : 'text-slate-300 hover:text-cyan-400'
                  }`}
                >
                  {section}
                </button>
              ))}
            </div>

            <button
              className="md:hidden text-slate-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-800/50">
              {['home', 'about', 'projects', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className="block w-full text-left py-2 capitalize text-slate-300 hover:text-cyan-400 transition-colors"
                >
                  {section}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <GeometricAvatar />
          
          <h1 className="text-4xl md:text-6xl font-bold text-slate-100 mb-4">
            Will Allwright
          </h1>
          <p className="text-xl md:text-2xl text-cyan-400 mb-8">
            Cloud Consultant & Cyber Assessor
          </p>
          <div className="flex justify-center space-x-6 mb-8">
            <a href="https://www.linkedin.com/in/william-allwright" className="text-slate-300 hover:text-cyan-400 transition-colors">
              <Linkedin size={24} />
            </a>
          </div>
          <button
            onClick={() => scrollToSection('projects')}
            className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-3 rounded-full hover:from-cyan-500 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-cyan-500/25"
          >
            View My Work
          </button>
          
          <div className="mt-16 animate-bounce">
            <ChevronDown size={32} className="text-slate-500 mx-auto" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-100 text-center mb-12">
            About Me
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-slate-300 text-lg leading-relaxed mb-6">
                I'm a results-driven and security-focused Cloud Consultant and Cyber Assessor with extensive experience 
                delivering secure, scalable cloud solutions and conducting comprehensive IT audits. I specialize in 
                managing Modern Work environments and implementing compliance frameworks such as ISO27001, Cyber Essentials, 
                and Cyber Assurance.
              </p>
              <p className="text-slate-300 text-lg leading-relaxed mb-6">
                With a First Class Honours degree in Business and Management from UWE Bristol, I bring strong analytical 
                skills and client-facing experience, taking a proactive approach to aligning technology and security with 
                business objectives. I excel at translating complex technical concepts into actionable business language.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 text-center border border-slate-700/50">
                  <div className="text-2xl font-bold text-cyan-400">2+</div>
                  <div className="text-slate-300">Years in Cyber</div>
                </div>
                <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 text-center border border-slate-700/50">
                  <div className="text-2xl font-bold text-cyan-400">Multiple</div>
                  <div className="text-slate-300">Certifications</div>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-slate-100 font-semibold mb-3">Cloud & Security</h3>
                <div className="flex flex-wrap gap-2">
                  {['Microsoft Azure', 'Microsoft 365', 'Intune', 'Defender', 'Purview'].map((skill) => (
                    <span key={skill} className="bg-slate-800/50 text-cyan-300 px-3 py-1 rounded-full text-sm border border-slate-700/50">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-slate-100 font-semibold mb-3">Compliance & Frameworks</h3>
                <div className="flex flex-wrap gap-2">
                  {['ISO27001', 'ISO42001', 'Cyber Essentials', 'CIS Controls', 'GDPR', 'PCI-DSS'].map((skill) => (
                    <span key={skill} className="bg-slate-800/50 text-blue-300 px-3 py-1 rounded-full text-sm border border-slate-700/50">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-slate-100 font-semibold mb-3">Certifications</h3>
                <div className="flex flex-wrap gap-2">
                  {['IASME Assessor', 'Cyber Assurance', 'Azure Fundamentals', 'M365 Fundamentals'].map((skill) => (
                    <span key={skill} className="bg-slate-800/50 text-emerald-300 px-3 py-1 rounded-full text-sm border border-slate-700/50">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-100 text-center mb-8">
            Featured Projects
          </h2>
          <p className="text-slate-300 text-center mb-12 max-w-2xl mx-auto">
            A selection of my key professional achievements and personal projects
          </p>
          
          <div className="flex justify-center gap-4 mb-12">
            <button
              onClick={() => navigateToPage('professional')}
              className="flex items-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-full hover:from-cyan-500 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-cyan-500/25"
            >
              <Briefcase size={20} />
              Professional Projects
            </button>
            <button
              onClick={() => navigateToPage('personal')}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-full hover:from-blue-500 hover:to-indigo-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-blue-500/25"
            >
              <User size={20} />
              Personal Projects
            </button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {mainProjects.map((project, index) => (
              <ProjectCard key={index} project={project} isProfessional={index < 2} />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-8">
            Let's Work Together
          </h2>
          <p className="text-slate-300 text-lg mb-12 max-w-2xl mx-auto">
            I'm always interested in new opportunities in cloud security and compliance. 
            Whether you have a cybersecurity challenge or want to discuss compliance frameworks, feel free to reach out!
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-2xl mx-auto">
            <button
              onClick={() => setIsContactFormOpen(true)}
              className="text-center p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-cyan-500/50 transition-all duration-300 transform hover:scale-105 cursor-pointer"
            >
              <Mail className="w-8 h-8 text-cyan-400 mx-auto mb-4" />
              <h3 className="text-slate-100 font-semibold mb-2">Email</h3>
              <p className="text-slate-300 text-sm">Send me a message</p>
            </button>
            
            <a
              href="https://www.linkedin.com/in/william-allwright"
              target="_blank"
              rel="noopener noreferrer"
              className="text-center p-6 bg-slate-800/50 border border-slate-700/50 rounded-xl hover:border-cyan-500/50 transition-all duration-300 transform hover:scale-105"
            >
              <Linkedin className="w-8 h-8 text-cyan-400 mx-auto mb-4" />
              <h3 className="text-slate-100 font-semibold mb-2">LinkedIn</h3>
              <p className="text-slate-300 text-sm">Connect with me</p>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-slate-800/50">
        <p className="text-slate-500">
          © 2025 Will Allwright. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Portfolio;